import * as React from "react";
import {SELECT_BORDER_BOX, SelectStyles} from "../../../components/common/etui_components_styles";
import {LockUnlockReason, lockUnlockReasonsEnum} from "../MassiveActionsTypesAndFunctions";
import {SelectInputOption} from "@vebox/etui-core";
import {
    ProcessStage,
    ProcessStageArrow,
    ProcessStageArrowDirection,
    ProcessStageOrder,
    useProcessController
} from "../../Processes";
import {VeboxForm} from "../../../components/form/VeboxForm";
import {DATA_STATE} from "../../../users/contexts/UsersDashboardContext";
import {VeBoxCard} from "../../../components/card/VeBoxCard";
import {FormCardFooter} from "../../common";
import {LockUnlockEquipmentCompressorContext} from "./LockUnlockEquipmentCompressorProvider";
import {Display} from "../../../components/Display";
import {AppTranslations} from "../../../components/Utils/translations/AppTranslations";
import {MassiveActionProcessState} from "../MassiveActionsProvider";

declare var AppTranslations: AppTranslations;


interface ChooseOperationAndReason {
    stepNumber: number
    stageOrder: ProcessStageOrder
    title: string
    cardTitle?: string
    operationTitle: string
    operationDescription: string
    massiveActionProcessState: MassiveActionProcessState
}

const lockUnlockReasonToSelectInputOption = (reasons: LockUnlockReason[]): SelectInputOption[] => {
    return reasons.map((reason) => {
        return {
            value: reason.key,
            label: reason.value.charAt(0).toUpperCase() + reason.value.slice(1)
        }
    })
}

const SELECT_BORDER_BOX_WITH_MARGIN: SelectStyles = {
    ...SELECT_BORDER_BOX,
    root: {
        mb: 10,
    },
};

export const ChooseOperationAndReason: React.FC<ChooseOperationAndReason> = ({
                                                                                 cardTitle,
                                                                                 title,
                                                                                 stepNumber,
                                                                                 stageOrder,
                                                                                 operationTitle,
                                                                                 operationDescription,
                                                                                 massiveActionProcessState
                                                                             }) => {

    const translations = AppTranslations.massiveActions.lockUnlockEquipmentCompressor
    const {lockUnlockReasons} = React.useContext(LockUnlockEquipmentCompressorContext);
    const processController = useProcessController();
    const AVAILABLE_OPERATIONS: SelectInputOption[] = [{
        value: lockUnlockReasonsEnum.LOCK_REASONS,
        label: translations.lockOperationName
    }, {value: lockUnlockReasonsEnum.UNLOCK_REASONS, label: translations.unlockOperationName}]

    const [values, setValues] = VeboxForm.useFormValueState<any>("changeCompressorStatus");
    const [lockUnlockReasonList, setLockUnlockReasonList] = React.useState<SelectInputOption[]>([]);
    const [isButtonDisabled, setIsButtonDisabled] = React.useState<boolean>(false);


    const reset = () => {
        if (AVAILABLE_OPERATIONS.length > 0) {
            updateValue("status", AVAILABLE_OPERATIONS[0]);
        }
        if (lockUnlockReasonList.length > 0) {
            updateValue("reason", lockUnlockReasonList[0]);
        }
    }

    const updateValue = (name: "status" | "reason", value: SelectInputOption) => {
        setValues((prevValues: SelectInputOption) => {
            return {
                ...prevValues,
                [name]: value
            }
        })
    }

    React.useEffect(() => {
        updateValue("status", AVAILABLE_OPERATIONS[0]);
    }, [])

    React.useEffect(() => {
        updateValue("reason", lockUnlockReasonList[0]);
    }, [lockUnlockReasonList])

    React.useEffect(() => {
        if (values?.status && lockUnlockReasons.reasonsState === DATA_STATE.loaded) {
            let reasons: SelectInputOption[] = [];
            switch (values?.status.value as lockUnlockReasonsEnum) {
                case lockUnlockReasonsEnum.LOCK_REASONS: {
                    reasons = lockUnlockReasonToSelectInputOption(lockUnlockReasons.reasons.equipmentLockReasons);
                    setLockUnlockReasonList(reasons)
                    break;
                }
                case lockUnlockReasonsEnum.UNLOCK_REASONS: {
                    reasons = lockUnlockReasonToSelectInputOption(lockUnlockReasons.reasons.equipmentUnlockReasons);
                    setLockUnlockReasonList(reasons)
                    break;
                }
            }
        }
    }, [values?.status, lockUnlockReasons.reasonsState])

    React.useEffect(() => {
        setIsButtonDisabled(!(values && values?.status && values?.reason))
    }, [values])

    React.useEffect(() => {
        if (massiveActionProcessState === MassiveActionProcessState.INITIAL) {
            reset();
        }
    }, [massiveActionProcessState])

    const goToNext = () => {
        processController.goTo(processController.position + 1)
    };

    return (
        <ProcessStage
            stepNumber={stepNumber}
            title={title}
            stageOrder={stageOrder}
        >
            <div className={"flx-row"}>
                <ProcessStageArrow stepNumber={stepNumber} direction={ProcessStageArrowDirection.BACKWARD}/>
                <VeBoxCard className={"form-card"}>
                    <Display isVisible={cardTitle !== undefined}>
                        <h2 className={"bold card-title"}>{cardTitle}</h2>
                    </Display>
                    <div className={"operation-container"}>
                        <VeboxForm.Section name={"changeCompressorStatus"}>
                            <VeboxForm.SelectInput
                                label={translations.equipmentCompressorStatusLabel}
                                name={"status"}
                                options={AVAILABLE_OPERATIONS}
                                styles={SELECT_BORDER_BOX_WITH_MARGIN}
                                value={values?.status}
                            />
                            <VeboxForm.SelectInput
                                label={translations.operationReason}
                                name={"reason"}
                                options={lockUnlockReasonList}
                                styles={SELECT_BORDER_BOX_WITH_MARGIN}
                                value={values?.reason}
                            />
                        </VeboxForm.Section>
                    </div>
                    <div className={"operation-description-container"}>
                        <h2 className={"bold"}>{operationTitle}</h2>
                        <p className={"operation-description"}>{operationDescription}</p>
                    </div>
                    <FormCardFooter
                        onReset={reset}
                        onNext={goToNext}
                        disabled={isButtonDisabled}
                    />
                </VeBoxCard>
                <ProcessStageArrow
                    stepNumber={stepNumber}
                    direction={ProcessStageArrowDirection.FORWARD}
                    visible={values?.status && values?.reason}
                />
            </div>
        </ProcessStage>
    )
}